import { useRef } from 'react'
import Cropper, { type ReactCropperElement } from 'react-cropper'
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react'
import 'cropperjs/dist/cropper.css'

import Button from '@components/Button'

interface PhotoEditProps {
  imageSrc: string | null
  open: boolean
  onSavePhoto: (photo: File) => void
  onClose: () => void
}

export default function PhotoEdit({ imageSrc, open, onSavePhoto, onClose }: PhotoEditProps) {
  const cropperRef = useRef<ReactCropperElement>(null)

  const getEditedImageBlob = async (): Promise<Blob> => {
    const cropper = cropperRef.current?.cropper

    return new Promise((resolve, reject) => {
      let croppedCanvas = cropper?.getCroppedCanvas()
      // If the cropped image is larger than 500x500, resize it to 500x500
      if (croppedCanvas && croppedCanvas?.width > 500) {
        croppedCanvas = cropper?.getCroppedCanvas({ width: 500, height: 500 })
      }
      croppedCanvas?.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob)
          } else {
            reject(new Error('Canvas to Blob conversion failed'))
          }
        },
        'image/png',
        1,
      ) // Specify mime type and quality
    })
  }

  async function handleSavePhoto() {
    const editedImageBlob = await getEditedImageBlob()
    const photoFile = new File([editedImageBlob], `photo.png`, { type: 'image/png' })

    onSavePhoto(photoFile)
  }

  return (
    <Dialog open={open} onClose={onClose} className='relative z-50'>
      <DialogBackdrop
        transition
        className='fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in'
      />

      <div className='fixed inset-0 z-10 w-screen overflow-y-auto'>
        <div className='flex mt-16 items-end justify-center text-center sm:items-center'>
          <DialogPanel
            transition
            className='w-full relative transform overflow-hidden bg-white m-1 p-1 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95'
          >
            <div className='rounded-lg'>
              <Cropper
                src={imageSrc ?? ''}
                // Cropper.js options
                aspectRatio={1}
                minCropBoxHeight={200}
                minCropBoxWidth={200}
                guides={false}
                ref={cropperRef}
                className='rounded-lg'
                style={{
                  borderRadius: '5px',
                }}
              />
            </div>
            <div className='my-2'>
              <Button label='Save Photo' variant='cta' onClick={handleSavePhoto} />
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  )
}
